import get from 'lodash/get';
import zipObject from 'lodash/zipObject';

export const filterKeysByValue = <T extends Object>(obj: T, qualifier: (obj: T) => boolean) =>
  Object.entries(obj)
    .filter(([, value]) => qualifier(value))
    .map(([key]) => key);

export function isObject<T>(obj: T) {
  return obj !== null && typeof obj === 'object';
}

export function mapObjectKeys<T extends Object>(
  object: T,
  mappedKeys: { [key in keyof T]: string }
) {
  const keys: string[] = [];
  const values: unknown[] = [];

  Object.entries(mappedKeys).forEach(([key, mappedKey]) => {
    const value = get(object, key, null);

    if (value) {
      keys.push(mappedKey);
      values.push(value);
    }
  });

  return zipObject(keys, values);
}

export function mapObjectValues<T extends Object>(
  object: T,
  mappedValueFunctions: { [key in keyof T]: (value: T[key]) => T[key] }
) {
  const mappedObject: T = { ...object };

  Object.entries(mappedObject).forEach(([key, value]) => {
    const mappedValueFunction = get(mappedValueFunctions, key, () => value);

    mappedObject[key as keyof T] = mappedValueFunction(value);
  });

  return mappedObject;
}

export function mapObjectKeyValues<T extends Object, U extends Object>(
  object: T,
  mappedKeys: { [key in keyof T]: keyof U },
  mappedValueFunctions: { [key in keyof U]: (value: U[key]) => U[key] }
) {
  const mappedObjectKeys = mapObjectKeys(object, mappedKeys) as { [key in keyof U]: U[key] };
  const mappedObjectValues = mapObjectValues(mappedObjectKeys, mappedValueFunctions);

  return mappedObjectValues;
}
